import { ref, computed } from 'vue';
import ViperConnectAPI from '../api/viperConnect';

export function useViperConnect(inboxId) {
  const status = ref('not_created');
  const instanceName = ref('');
  const connectedAt = ref(null);
  const lastCheckAt = ref(null);
  const settings = ref({
    always_online: true,
    read_messages: true,
    read_status: false,
    human_takeover_pauses_bot: true,
  });
  const qrCode = ref(null);
  const logs = ref([]);

  const isLoading = ref(false);
  const isCreating = ref(false);
  const isTesting = ref(false);
  const error = ref(null);

  let qrPollingInterval = null;
  const QR_POLLING_INTERVAL = 3000; // 3 seconds
  const QR_EXPIRY_SECONDS = 60;
  const qrTimeRemaining = ref(QR_EXPIRY_SECONDS);

  // Computed
  const isConnected = computed(() => status.value === 'connected');
  const isDisconnected = computed(() => status.value === 'disconnected');
  const isQrPending = computed(() => status.value === 'qr_pending');
  const isNotCreated = computed(() => status.value === 'not_created' || !status.value);

  const statusLabel = computed(() => {
    switch (status.value) {
      case 'connected':
        return 'Conectado';
      case 'disconnected':
        return 'Desconectado';
      case 'qr_pending':
        return 'Aguardando conexão';
      default:
        return 'Não configurado';
    }
  });

  const statusColor = computed(() => {
    switch (status.value) {
      case 'connected':
        return 'green';
      case 'disconnected':
        return 'red';
      case 'qr_pending':
        return 'yellow';
      default:
        return 'gray';
    }
  });

  // Methods
  async function fetchStatus() {
    if (!inboxId.value) return;

    isLoading.value = true;
    error.value = null;

    try {
      const { data } = await ViperConnectAPI.getStatus(inboxId.value);
      status.value = data.status || 'not_created';
      instanceName.value = data.instance_name || '';
      connectedAt.value = data.connected_at;
      lastCheckAt.value = data.last_check_at;
      settings.value = {
        always_online: data.settings?.always_online !== false,
        read_messages: data.settings?.read_messages !== false,
        read_status: data.settings?.read_status === true,
        human_takeover_pauses_bot: data.settings?.human_takeover_pauses_bot !== false,
      };
    } catch (err) {
      error.value = err.response?.data?.error || 'Erro ao carregar status';
    } finally {
      isLoading.value = false;
    }
  }

  async function createInstance() {
    if (!inboxId.value) return;

    isCreating.value = true;
    error.value = null;

    try {
      const { data } = await ViperConnectAPI.createInstance(inboxId.value, settings.value);

      if (data.success) {
        status.value = 'qr_pending';
        instanceName.value = data.instance_name;
        qrCode.value = data.qrcode;
        startQrPolling();
      } else {
        error.value = data.error || 'Erro ao criar instância';
      }
    } catch (err) {
      error.value = err.response?.data?.error || 'Erro ao criar instância';
    } finally {
      isCreating.value = false;
    }
  }

  async function refreshQrCode() {
    if (!inboxId.value) return;

    isLoading.value = true;
    error.value = null;

    try {
      const { data } = await ViperConnectAPI.getQrCode(inboxId.value);
      qrCode.value = data.qrcode;
      qrTimeRemaining.value = QR_EXPIRY_SECONDS;

      // Check if status changed (e.g., connected)
      if (data.status === 'open' || data.status === 'connected') {
        stopQrPolling();
        await fetchStatus();
      }
    } catch (err) {
      error.value = err.response?.data?.error || 'Erro ao atualizar QR Code';
    } finally {
      isLoading.value = false;
    }
  }

  async function testConnection() {
    if (!inboxId.value) return;

    isTesting.value = true;
    error.value = null;

    try {
      const { data } = await ViperConnectAPI.testConnection(inboxId.value);
      status.value = data.status;

      return data.success;
    } catch (err) {
      error.value = err.response?.data?.error || 'Erro ao testar conexão';
      return false;
    } finally {
      isTesting.value = false;
    }
  }

  async function updateSettings() {
    if (!inboxId.value) return;

    isLoading.value = true;
    error.value = null;

    try {
      const { data } = await ViperConnectAPI.updateSettings(
        inboxId.value,
        settings.value
      );
      return data.success;
    } catch (err) {
      error.value = err.response?.data?.error || 'Erro ao salvar configurações';
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  async function disconnect() {
    if (!inboxId.value) return;

    isLoading.value = true;
    error.value = null;

    try {
      await ViperConnectAPI.disconnect(inboxId.value);
      status.value = 'not_created';
      instanceName.value = '';
      connectedAt.value = null;
      lastCheckAt.value = null;
      qrCode.value = null;
      stopQrPolling();
    } catch (err) {
      error.value = err.response?.data?.error || 'Erro ao desconectar';
    } finally {
      isLoading.value = false;
    }
  }

  async function fetchLogs() {
    if (!inboxId.value) return;

    try {
      const { data } = await ViperConnectAPI.getLogs(inboxId.value);
      logs.value = data;
    } catch (err) {
      // Silent fail for logs
      console.error('Error fetching logs:', err);
    }
  }

  function startQrPolling() {
    stopQrPolling();
    qrTimeRemaining.value = QR_EXPIRY_SECONDS;

    qrPollingInterval = setInterval(async () => {
      qrTimeRemaining.value -= 3;

      if (qrTimeRemaining.value <= 0) {
        // QR expired, refresh and check status
        qrTimeRemaining.value = QR_EXPIRY_SECONDS;
        try {
          const { data } = await ViperConnectAPI.getQrCode(inboxId.value);
          qrCode.value = data.qrcode;
          // QR code endpoint also returns Evolution API status
          if (data.status === 'open' || data.status === 'connected') {
            status.value = 'connected';
            stopQrPolling();
            await fetchStatus(); // Refresh all data
            return;
          }
        } catch {
          // Continue polling
        }
      }

      // Also check database status
      try {
        const { data } = await ViperConnectAPI.getStatus(inboxId.value);
        if (data.status === 'connected') {
          status.value = 'connected';
          connectedAt.value = data.connected_at;
          stopQrPolling();
        }
      } catch {
        // Continue polling
      }
    }, QR_POLLING_INTERVAL);
  }

  function stopQrPolling() {
    if (qrPollingInterval) {
      clearInterval(qrPollingInterval);
      qrPollingInterval = null;
    }
  }

  return {
    // State
    status,
    instanceName,
    connectedAt,
    lastCheckAt,
    settings,
    qrCode,
    qrTimeRemaining,
    logs,
    isLoading,
    isCreating,
    isTesting,
    error,

    // Computed
    isConnected,
    isDisconnected,
    isQrPending,
    isNotCreated,
    statusLabel,
    statusColor,

    // Methods
    fetchStatus,
    createInstance,
    refreshQrCode,
    testConnection,
    updateSettings,
    disconnect,
    fetchLogs,
    startQrPolling,
    stopQrPolling,
  };
}

